import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { auth } from './Firebase';
import { Link as RouterLink } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, TableSortLabel } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Helmet } from 'react-helmet-async';
import CustomSelect from './CustomSelect';

// Pomocnicza funkcja do formatowania daty, identyczna jak we wzorcowym pliku WcPositionMatrix.js
const convertUTCToLocal = (utcValue) => {
  if (!utcValue) return '';
  let utcDate;
  if (typeof utcValue === 'number') {
    utcDate = new Date(utcValue);
  } else if (typeof utcValue === 'string') {
    utcDate = new Date(utcValue.replace(' ', 'T') + 'Z');
  } else {
    return '';
  }
  return utcDate.toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' });
};

// Oryginalny gradient kolorów z pliku WcPositionMatrix.js - dokładnie według wytycznych
const getGradientColor = (value) => {
  if (value === 0) return 'rgba(255, 255, 255, 0.3)'; // Pojaśniony neutralny szary
  const scaledValue = Math.max(0, value / 0.8);
  const grayBase = 255;
  const r = Math.round(grayBase * (1 - scaledValue));
  const g = Math.round(grayBase * (1 - scaledValue) + 255 * scaledValue);
  const b = Math.round(grayBase * (1 - scaledValue) + 50 * scaledValue); // Lekkie niebieskie dla głębi
  const alpha = 0.3 + scaledValue * 0.7;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatChance = (chance, useTilde = false) => {
  if (typeof chance !== 'number' || isNaN(chance)) return '—';

  const percent = chance * 100;

  if (percent < 0.001) return useTilde ? '~0.0%' : '0.0%';
  if (percent > 99.999) return useTilde ? '~100%' : '100.0%';

  const decimals = ((percent < 0.1 && percent > 0) || (percent > 99.9 && percent < 100)) ? 2 : 1;
  const formatted = percent.toFixed(decimals);

  return useTilde && (percent < 0.01 || percent > 99.99)
    ? `~${formatted}%`
    : `${formatted}%`;
};

// Definicja nagłówków wierszy dla różnicy bramek (posortowane malejąco od najlepszego bilansu)
const ROW_GD_DEFINITION = ['>3', '3', '2', '1', '0', '-1', '-2', '-3', '-4', '-5', '-6', '-7', '<-7' ];

// Funkcja pomocnicza mapująca surową różnicę bramek na odpowiedni przedział/grupę
const getGoalDifferenceGroupKey = (gd) => {
  if (gd <= -8) return '<-7';
  if (gd >= 4) return '>3';
  return String(gd);
};

const WcPointThreshold = () => {
  const [simulationData, setSimulationData] = useState(null);
  const [matrixRows, setMatrixRows] = useState([]);
  const [columnsPoints, setColumnsPoints] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSimulationId, setSelectedSimulationId] = useState('');
  const [error, setError] = useState(null);
  
  const simulationOptions = simulations.map((sim) => ({
    value: sim.simulation_id,
    label: sim.displayName
  }));

useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/competition/431/simulations`
      )
      .then((response) => {
        const sims = response.data || [];

        // Mapowanie danych (dodanie userId i gwiazdki dla symulacji warunkowych)
        const simList = sims.map(sim => ({
          ...sim,
          userId: sim.user_id, 
          displayName: sim.conditions && sim.conditions !== "" 
            ? `${convertUTCToLocal(sim.created_at)} (*)` 
            : convertUTCToLocal(sim.created_at)
        }));

        // Filtrowanie symulacji (domyślne oraz należące do zalogowanego użytkownika)
        const filteredSimList = simList.filter(sim => {
          const isDefault = !sim.userId || sim.userId === ""; 
          const isOwned = auth.currentUser?.uid && sim.userId === auth.currentUser.uid;
          return isDefault || isOwned;
        });

        setSimulations(filteredSimList);

        if (filteredSimList.length > 0) {
          setSelectedSimulationId(filteredSimList[0].simulation_id);
        }
      })
      .catch((err) => {
        console.error('Błąd pobierania listy symulacji:', err);
      });
  }, [auth.currentUser]);

  useEffect(() => {
	if (!selectedSimulationId) return;

    setLoading(true);

    axios.get(`${process.env.REACT_APP_API_URL}/api/worldcup/3rd-threshold?simulationId=${selectedSimulationId}`)
      .then(response => {
        setSimulationData(response.data);
        setLoading(false);
        setInitialLoading(false);
        
        const rawThresholds = response.data.thresholds || [];
        
        // Dynamiczne wyciągnięcie i posortowanie punktów, które będą kolumnami (rosnąco)
        const uniquePoints = Array.from(new Set(rawThresholds.map(item => item.points))).sort((a, b) => a - b);
        setColumnsPoints(uniquePoints);
        
        // 1. Agregacja i grupowanie danych bramek (<-5 oraz >5) w strukturze wierszy (różnica bramek)
        const groupedData = {}; // Struktura: { [grupa_bramkowa]: { [punkty]: { total, advanced } } }

        ROW_GD_DEFINITION.forEach(gd => {
          groupedData[gd] = {};
          uniquePoints.forEach(pts => {
            groupedData[gd][pts] = { totalOccurrences: 0, advancedCount: 0, hasData: false };
          });
        });

        rawThresholds.forEach(item => {
          const pts = item.points;
          const gd = item.goalDifference;
          const groupKey = getGoalDifferenceGroupKey(gd);

          if (groupedData[groupKey] && groupedData[groupKey][pts]) {
            groupedData[groupKey][pts].totalOccurrences += item.totalOccurrences;
            groupedData[groupKey][pts].advancedCount += item.advancedCount;
            groupedData[groupKey][pts].hasData = true;
          }
        });

        // 2. Budowanie wierszy macierzy na podstawie różnicy bramek (zamiana miejscami osi)
        const rows = ROW_GD_DEFINITION.map(gd => {
          const columnsData = uniquePoints.map(pts => {
            const cell = groupedData[gd][pts];
            
            // Jeśli w symulacji ani razu nie wystąpił dany przypadek punktów i bramek
            if (!cell || (!cell.hasData && cell.totalOccurrences === 0)) {
              return { probability: null, total: 0 };
            }

            // Obliczenie wypadkowego prawdopodobieństwa dla grupy
            const probability = cell.totalOccurrences > 0 
              ? cell.advancedCount / cell.totalOccurrences 
              : 0;

            return { probability, total: cell.totalOccurrences, advanced: cell.advancedCount };
          });

          return {
            goalDifference: gd,
            columnsData: columnsData
          };
        });

        setMatrixRows(rows);
        setLoading(false);
      })
      .catch(err => {
        console.error("Błąd ładowania progów punktowych:", err);
        setError("Nie udało się pobrać danych progów punktowych.");
        setLoading(false);
      });
  }, [selectedSimulationId]);
  
    const currentSimulationIndex = simulations.findIndex(
	  sim => sim.simulation_id === selectedSimulationId
	);

  
  const handleSimulationSelect = (event) => {
	  const simulationId = event.target.value;

	  const index = simulations.findIndex(
		sim => sim.simulation_id === simulationId
	  );

	  //setCurrentSimulationIndex(index);
	  setSelectedSimulationId(simulationId);
	};

	const handlePreviousSimulation = () => {
	  if (currentSimulationIndex < simulations.length - 1) {
		const newIndex = currentSimulationIndex + 1;

		//setCurrentSimulationIndex(newIndex);
		setSelectedSimulationId(
		  simulations[newIndex].simulation_id
		);
	  }
	};

	const handleNextSimulation = () => {
	  if (currentSimulationIndex > 0) {
		const newIndex = currentSimulationIndex - 1;

		//setCurrentSimulationIndex(newIndex);
		setSelectedSimulationId(
		  simulations[newIndex].simulation_id
		);
	  }
	};	

  if (initialLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ color: '#ffffff' }}>Ładowanie progów punktowych...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Helmet>
        <title>Progi punktowe tabeli 3. miejsc - SymuLiga</title>
      </Helmet>

      {/* Nagłówek i metadane symulacji, identyczne jak w WcPositionMatrix */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ color: '#ffffff', fontWeight: 'bold', mb: 1, fontSize: { xs: '1.5rem', sm: '2.1rem' } }}>
          Szansa awansu z 3. miejsca w grupie
        </Typography>
        {simulations.length > 0 && (
		   <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            <IconButton
              onClick={handlePreviousSimulation}
              disabled={currentSimulationIndex >= simulations.length - 1}
              size="small"
            >
              <ArrowBackIosIcon fontSize="small" />
            </IconButton>
			  <CustomSelect
				  value={selectedSimulationId}
				  onChange={handleSimulationSelect}
				  options={simulationOptions}
				/>
			<IconButton
              onClick={handleNextSimulation}
              disabled={currentSimulationIndex === 0}
              size="small"
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
		)}
      </Box>

		{simulations[currentSimulationIndex]?.conditions !== "" && (
            <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, textAlign: 'center' }}>
              Symulacja warunkowa!<br />
              <span dangerouslySetInnerHTML={{ __html: simulations[currentSimulationIndex]?.conditions?.replace(/\n/g, '<br />') || '' }} />
            </Typography>
          )}

      {/* Kontener tabeli ze stylem zaczerpniętym z pliku wzorcowego */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', maxWidth: 'max-content', margin: '0 auto', overflowX: 'auto', boxShadow: 3, borderRadius: 2 }}>
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            {/* Pierwszy wiersz nagłówka informujący o sekcji punktów w kolumnach */}
            <TableRow>
              <TableCell sx={{ p: '4px', backgroundColor: '#2a2a2a', borderBottom: '2px solid #3a3a3a' }} />
              <TableCell 
                colSpan={columnsPoints.length} 
                align="center" 
                sx={{ color: '#ffffff', fontWeight: 'bold', backgroundColor: '#252525', borderBottom: '1px solid #3a3a3a', fontSize: '0.9rem', letterSpacing: 1, p: '4px' }}
              >
                PUNKTY
              </TableCell>
            </TableRow>
            {/* Właściwy wiersz nagłówków kolumn */}
            <TableRow>
              <TableCell align="center" sx={{ color: '#ffffff', fontWeight: 'bold', backgroundColor: '#2a2a2a', minWidth: 100, borderBottom: '2px solid #444', p: '6px 4px' }}>
                Bramki
              </TableCell>
              {columnsPoints.map((pts, index) => (
                <TableCell key={index} align="center" sx={{ color: '#ffffff', fontWeight: 'bold', backgroundColor: '#252525', minWidth: 50, borderBottom: '2px solid #444', fontSize: '0.85rem', p: '6px 2px' }}>
                  {pts} pkt
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          
          <TableBody>
            {matrixRows.map((row, rowIndex) => (
              <TableRow 
                key={rowIndex} 
                sx={{ '&:hover': { backgroundColor: '#2a2a2a' }, transition: 'background-color 0.2s' }}
              >
                {/* Pierwsza kolumna - Różnica bramek (Wiersz / Wyróżniona) */}
                <TableCell 
                  align="center" 
                  sx={{ 
                    color: '#ffffff', 
                    fontWeight: 'bold', 
                    backgroundColor: '#1e1e1e', 
                    borderRight: '2px solid rgba(255, 255, 255, 0.1)',
                    borderBottom: '1px solid #3a3a3a',
                    fontSize: '1rem',
                    p: '5px 4px',
                    minWidth: 100
                  }}
                >
                  {row.goalDifference}
                </TableCell>

                {/* Komórki prawdopodobieństw z gradientem i efektami hover ze skryptu wzorcowego */}
                {row.columnsData.map((cell, colIndex) => {
                  const isAvailable = cell.probability !== null;
                  
                  return (
                    <TableCell
                      key={colIndex}
                      align="center"
                      sx={{
                        width: 'auto',
                        minWidth: 50,
                        backgroundColor: isAvailable ? getGradientColor(cell.probability) : 'rgba(255, 255, 255, 0.05)',
                        color: isAvailable ? 'black' : '#666666',
                        fontWeight: 'bold',
                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                        padding: '5px 2px',
                        textShadow: isAvailable ? '0.5px 0.5px 1px rgba(0, 0, 0, 0.3)' : 'none',
                        borderBottom: '1px solid #3a3a3a',
                        transition: 'all 0.2s',
                        '@media (hover: hover)': {
                          '&:hover': isAvailable ? {
                            backgroundColor: getGradientColor(Math.min(cell.probability + 0.1, 1)),
                            transform: 'scale(1.05)',
                            cursor: 'pointer'
                          } : {}
                        }
                      }}
                      title={isAvailable ? `Liczba przypadków w symulacji: ${cell.advanced} / ${cell.total}` : 'Brak takiego przypadku'}
                    >
                      {isAvailable ? formatChance(cell.probability) : '-'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default WcPointThreshold;